import React, { useState, useEffect } from 'react';
import { Bell, Clock, MessageSquare, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface SessionReminderSettingsProps {
  profile: any;
  onUpdate?: () => void;
}

export const SessionReminderSettings: React.FC<SessionReminderSettingsProps> = ({
  profile,
  onUpdate
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [settings, setSettings] = useState({
    reminder_enabled: profile?.reminder_enabled ?? true,
    reminder_hours_before: profile?.reminder_hours_before ?? 24,
    reminder_message: profile?.reminder_message ?? 'This is a friendly reminder of your upcoming therapy session.',
    second_reminder_enabled: profile?.second_reminder_enabled ?? false,
    second_reminder_hours: profile?.second_reminder_hours ?? 2
  });

  useEffect(() => {
    if (profile) {
      setSettings({
        reminder_enabled: profile.reminder_enabled ?? true,
        reminder_hours_before: profile.reminder_hours_before ?? 24,
        reminder_message: profile.reminder_message ?? 'This is a friendly reminder of your upcoming therapy session.',
        second_reminder_enabled: profile.second_reminder_enabled ?? false,
        second_reminder_hours: profile.second_reminder_hours ?? 2
      });
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('clinician_profiles')
        .update(settings)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinician-profile'] });
      toast({
        title: 'Settings saved',
        description: 'Your reminder preferences have been updated.'
      });
      onUpdate?.();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const updateField = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Session Reminders
        </CardTitle>
        <CardDescription>
          Customise how and when clients receive appointment reminders
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Primary Reminder */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Enable Reminders</Label>
              <p className="text-sm text-muted-foreground">
                Send automatic reminders to clients before sessions
              </p>
            </div>
            <Switch
              checked={settings.reminder_enabled}
              onCheckedChange={(checked) => updateField('reminder_enabled', checked)}
            />
          </div>

          {settings.reminder_enabled && (
            <div className="pl-4 border-l-2 border-primary/20 space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Hours Before Session
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={settings.reminder_hours_before}
                    onChange={(e) => updateField('reminder_hours_before', parseInt(e.target.value) || 24)}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Common choices: 24 hours (1 day), 48 hours (2 days), or 72 hours (3 days)
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Reminder Message
                </Label>
                <Textarea
                  value={settings.reminder_message}
                  onChange={(e) => updateField('reminder_message', e.target.value)}
                  rows={3}
                  placeholder="Your custom reminder message..."
                />
                <p className="text-xs text-muted-foreground">
                  This message will be included in the reminder email. Session details are added automatically.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Second Reminder */}
        {settings.reminder_enabled && (
          <div className="space-y-4 pt-4 border-t border-border/30">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Second Reminder</Label>
                <p className="text-sm text-muted-foreground">
                  Send an additional reminder closer to the session
                </p>
              </div>
              <Switch
                checked={settings.second_reminder_enabled}
                onCheckedChange={(checked) => updateField('second_reminder_enabled', checked)}
              />
            </div>

            {settings.second_reminder_enabled && (
              <div className="pl-4 border-l-2 border-accent/20 space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Hours Before Session
                  </Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min={1}
                      max={24}
                      value={settings.second_reminder_hours}
                      onChange={(e) => updateField('second_reminder_hours', parseInt(e.target.value) || 2)}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">hours</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Typically 1-2 hours before the session as a final reminder
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        <div className="bg-muted/30 rounded-lg p-4 mt-4">
          <p className="text-sm font-medium mb-2">Preview</p>
          <div className="text-sm text-muted-foreground space-y-1">
            {settings.reminder_enabled ? (
              <>
                <p>✓ First reminder: {settings.reminder_hours_before} hours before session</p>
                {settings.second_reminder_enabled && (
                  <p>✓ Second reminder: {settings.second_reminder_hours} hours before session</p>
                )}
              </>
            ) : (
              <p>⚠️ Reminders are currently disabled</p>
            )}
          </div>
        </div>

        <Button 
          onClick={() => updateMutation.mutate()} 
          disabled={updateMutation.isPending}
          className="w-full gap-2"
        >
          <Save className="h-4 w-4" />
          {updateMutation.isPending ? 'Saving...' : 'Save Reminder Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SessionReminderSettings;